import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate, parseCategories, getImageUrl } from "../utils/helpers";
import { InlineShareButtons } from "../components/ShareButton";
import MetaTags from "../components/MetaTags";

const CategoryPage = ({ allArticles, lang, textData, imageVersion }) => {
  const { categoryName } = useParams();
  const navigate = useNavigate();

  const displayTitle = useMemo(() => {
    const navLinks = textData.nav;
    const foundItem = navLinks.find((item) => {
      const lowerItem = item.toLowerCase();
      if (lowerItem === "ahmedabad" && categoryName === "localnews")
        return true;
      if (lowerItem === "sports" && categoryName === "sportsnews") return true;
      if (lowerItem === "lifestyle" && categoryName === "other") return true;
      return lowerItem === categoryName;
    });
    return foundItem || categoryName;
  }, [categoryName, textData.nav]);

  const articlesForCategory = allArticles.filter((article) => {
    if (!article.categories) {
      return false;
    }

    const categories = parseCategories(article.categories);
    const lowerCaseCategories = categories.map((c) => String(c).toLowerCase());
    const lowerCaseCategoryName = categoryName.toLowerCase();

    if (lowerCaseCategoryName === "world") {
      return lowerCaseCategories.includes("worldnews");
    }

    return lowerCaseCategories.includes(lowerCaseCategoryName);
  });

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags
        title={`${displayTitle} News`}
        description={`Latest news and updates from the ${displayTitle} category. Stay informed with Amdavad Post.`}
      />
      <h1 className="text-3xl font-bold mb-6 capitalize border-b-2 border-red-600 pb-2">
        {displayTitle} News
      </h1>
      {articlesForCategory.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articlesForCategory.map((news) => (
            <div
              key={news.id}
              className="bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 relative"
            >
              <div
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    navigate(`/news/${news.slug}`);
                }}
                onClick={() => navigate(`/news/${news.slug}`)}
                className="cursor-pointer"
              >
                <img
                  src={getImageUrl(news, categoryName, imageVersion)}
                  alt={news.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="mb-2 flex flex-wrap gap-2">
                    {(parseCategories(news.categories) || []).map((c, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full capitalize"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  <h3 className="font-bold text-lg mb-2 h-20 overflow-hidden">
                    {news.title}
                  </h3>
                  <div className="text-sm text-gray-500 flex items-center justify-between mt-4">
                    <span>{formatDate(news.createdAt)}</span>
                    <div className="flex items-center gap-3">
                      <div onClick={(e) => e.stopPropagation()}>
                        <InlineShareButtons
                          url={`${window.location.origin}/news/${news.slug}`}
                          title={news.title}
                        />
                      </div>
                      <span className="font-semibold text-red-700 hover:text-red-900">
                        Read More &rarr;
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>No articles found in this category.</p>
      )}
    </main>
  );
};

export default CategoryPage;
